import React from 'react';

export default function WizardPreviewSetForm({ principleData, actualPrinciplesData, peopleDetailsData, customizationData, onDataChange, initialData }) {
    const getImageSrc = (base64String, fileName) => {
        if (!base64String) return null;
        try {
            // Determine file type from fileName
            if (fileName) {
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                if (fileExtension === 'pdf') {
                    return `data:application/pdf;base64,${base64String}`;
                } else if (['jpg', 'jpeg'].includes(fileExtension)) {
                    return `data:image/jpeg;base64,${base64String}`;
                } else if (fileExtension === 'png') {
                    return `data:image/png;base64,${base64String}`;
                }
            }
            // Default to jpeg if no extension
            return `data:image/jpeg;base64,${base64String}`;
        } catch (e) {
            return null;
        }
    };

    const people = peopleDetailsData?.people || [];
    const showGivenToSelected = peopleDetailsData?.showGivenToSelected || false;
    const showGivesToSelected = peopleDetailsData?.showGivesToSelected || false;
    
    // Determine labels based on selection
    const mainLabel = showGivesToSelected ? 'Gives' : (showGivenToSelected ? 'Given' : '');
    const branchLabel = 'To';

    // Helper function to strip HTML tags and get plain text
    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Check if we should show actual principles instead of the principle name
    const isShowActualPrinciple = principleData?.selectedPrinciple === 'Show Actual Principle';
    const isShowDerivedPrinciple = principleData?.selectedPrinciple === 'Show Derived Principle';
    const selectedActualPrinciples = actualPrinciplesData?.selectedActualPrinciples || [];
    const typedPrincipleText = customizationData?.principleText ? stripHtml(customizationData.principleText).trim() : '';

    // For "Show Derived Principle", show actual principles + typed text at the bottom
    const getDisplayPrinciples = () => {
        if (isShowDerivedPrinciple) {
            const principles = [...selectedActualPrinciples];
            // Add the typed text from the "Type Principle" step at the bottom if it exists
            if (typedPrincipleText) {
                principles.push(typedPrincipleText);
            }
            return principles;
        } else if (isShowActualPrinciple) {
            return selectedActualPrinciples;
        }
        return [];
    };

    const displayPrinciples = getDisplayPrinciples();
    // Only show principles list if there's at least one item to display
    const shouldShowPrinciplesList = (isShowActualPrinciple && selectedActualPrinciples.length > 0) || 
                                     (isShowDerivedPrinciple && displayPrinciples.length > 0);

    return (
        <div className="wizard-preview-set-form">
            <div className="preview-set-visual">
                <div className="given-set-container">
                    <div className="given-set-dot"></div>
                    <div className="given-set-box">
                        {shouldShowPrinciplesList ? (
                            <div className="given-set-actual-principles-list">
                                {displayPrinciples.map((principle, index) => (
                                    <div key={index} className="given-set-actual-principle-item">
                                        <span className="given-set-principle-number">{index + 1}.</span>
                                        <span className="given-set-principle-text">{principle}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <h3 className="given-set-title">{principleData?.selectedPrinciple || 'The Given Set'}</h3>
                        )}
                    </div>
                </div>

                {people.length > 0 && (
                    <div className="flowchart-container">
                        {/* Vertical line from box */}
                        <div className="vertical-line">
                            {mainLabel && <span className="line-label-main">{mainLabel}</span>}
                        </div>

                        {/* Wrapper for horizontal line and branches to ensure same width */}
                        <div className="flowchart-horizontal-wrapper">
                            {/* Horizontal connector line */}
                            <div className="horizontal-line"></div>

                            {/* Branch lines and profiles */}
                            <div className="flowchart-branches">
                            {people.map((person, index) => {
                                const imageSrc = getImageSrc(person.image, person.fileName);
                                const isPdf = person.fileName?.toLowerCase().endsWith('.pdf');
                                return (
                                    <div key={index} className="flowchart-branch">
                                        <div className="branch-vertical-line">
                                            {(showGivenToSelected || showGivesToSelected) && (
                                                <span className="line-label-branch">{branchLabel}</span>
                                            )}
                                            <div className="branch-arrow"></div>
                                        </div>
                                        <div className="person-profile">
                                            {imageSrc ? (
                                                isPdf ? (
                                                    <div className="profile-pdf">
                                                        <span>PDF</span>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={imageSrc}
                                                        alt={person.name || `Person ${index + 1}`}
                                                        className="profile-image"
                                                    />
                                                )
                                            ) : (
                                                <div className="profile-placeholder">
                                                    <span>No Image</span>
                                                </div>
                                            )}
                                            <div className="profile-name">{person.name || `Person ${index + 1}`}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    </div>
                )}

                {people.length === 0 && (
                    <div className="no-people-message">
                        <p>No people added yet. Please go back to "Select People Details" to add people.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
